// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { BorderedCheckFilledIcon } from '@iheartjane/menu';
import type { Spacing } from '@iheartjane/reefer';
import { Box, Typography } from '@iheartjane/reefer';
import { spacing } from '@iheartjane/reefer-emotion';
import type { PriceId } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';

import type { WeightSelectorWeight } from '@jane/shared-ecomm/types';
import { labelForWeightId } from '@jane/shared/util';

import { useProductCardContext } from '../productCardProvider';

const WeightButtonItem = styled.button<{
  fullWidth?: boolean;
  selected: boolean;
  translateAmount: number;
  width: number;
  xMargin: number;
}>(
  {
    height: 54,
    outline: '0 !important',
    borderRadius: 4,
    position: 'relative',
    textAlign: 'left',
    flex: '0 0 auto',
    padding: '3px 3px',
    cursor: 'pointer',
  },
  ({ width, xMargin, fullWidth, theme }) => [
    {
      border: `1px solid ${theme.colors.grays.light}`,
      borderRadius: theme.borderRadius.lg,
    },
    spacing({ mx: xMargin as Spacing }),
    fullWidth && { flex: '1 0 auto' },
    { minWidth: width },
  ],
  ({ selected, translateAmount, theme }) => [
    {
      backgroundColor: selected
        ? theme.colors.grays.white
        : 'rgba(255, 255, 255, .9)',
      color: selected ? theme.colors.primary.main : theme.colors.grays.black,
      '@media (hover: hover)': {
        '&:hover': {
          background: 'rgba(255, 255, 255, .8)',
        },
      },
      '&:focus-visible': {
        background: theme.colors.grays.light,
        borderRadius: theme.borderRadius.lg,
      },
    },
    selected && {
      borderColor: theme.colors.primary.main,
      borderWidth: '3px !important',
    },
    {
      transition: 'transform .2s',
      transform: `translateX(${translateAmount}px)`,
    },
  ]
);

const WeightsButtonItems = styled.div<{ fullWidth?: boolean }>(
  spacing({ p: 8 }),
  {
    display: 'flex',
    overflow: 'hidden',
  },
  ({ fullWidth }) =>
    fullWidth && [{ justifyContent: 'space-around', width: '100%' }]
);

interface Props {
  fullWidth?: boolean;
  icon?: boolean;
  itemWidth?: number;
  labelId: string;
  onChange: (priceId: PriceId) => void;
  selectedWeight: PriceId;
  translateAmount?: number;
  weights: WeightSelectorWeight[];
  xMargin?: number;
}

export const WeightPriceItems = ({
  fullWidth,
  itemWidth = 88,
  labelId,
  onChange,
  selectedWeight,
  translateAmount = 0,
  weights,
  xMargin = 2,
}: Props) => {
  const { cartProduct, menuProduct: product } = useProductCardContext();
  const weightsInCart = (cartProduct || []).map((product) => product.price_id);

  return (
    <WeightsButtonItems
      fullWidth={fullWidth}
      role="radiogroup"
      aria-labelledby={labelId}
    >
      {weights.map((weight) => {
        const selected = weight.value === selectedWeight;
        const inCart = weightsInCart.includes(weight.value);
        const weightLabel = labelForWeightId(weight.value, product?.amount);
        return (
          <WeightButtonItem
            key={weight.value}
            selected={selected}
            translateAmount={translateAmount}
            width={itemWidth}
            xMargin={xMargin}
            fullWidth={fullWidth}
            onClick={() => onChange(weight.value)}
            role="radio"
            aria-checked={selected}
          >
            <Box
              mt={selected ? -2 : 0}
              ml={selected ? -2 : 0}
              style={{ textAlign: 'center' }}
            >
              <Typography
                color="text-light"
                as="div"
                mb={-2}
                variant="small-body-regular"
                width="100%"
              >
                {weightLabel}
              </Typography>
              <Typography as="div" variant="small-body-semibold">
                {formatCurrency(weight.price)}
              </Typography>
            </Box>
            {inCart && <BorderedCheckFilledIcon top={-4} right={-3} />}
          </WeightButtonItem>
        );
      })}
    </WeightsButtonItems>
  );
};
